/**
 * CustomerProjectsInline
 *
 * Embedded inside the Customer edit dialog so admins can manage QBO-style
 * Projects (e.g. "Plumbing 2025", "HVAC Q3", "Janitorial") right next to
 * the customer they belong to — no page-hop.
 *
 * Shows existing projects with their number/name/status and a compact "+"
 * row that adds a new project. The "Also create in QBO" checkbox is on by
 * default when QBO is connected; on Save we POST the project locally and
 * then optionally fire-and-forget /projects/{id}/push-to-quickbooks.
 *
 * Read-only when no customerId yet (customer must be saved first to attach
 * projects to it).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Briefcase, Plus, Loader2, RefreshCw, X, Archive, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

export default function CustomerProjectsInline({ customerId, token, qbConnected }) {
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ project_number: '', name: '', also_push: !!qbConnected });
  const [saving, setSaving] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!customerId) {
      setProjects([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/projects?customer_id=${customerId}&include_archived=false`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Could not load projects', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [customerId, token, toast]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const resetDraft = () => {
    setDraft({ project_number: '', name: '', also_push: !!qbConnected });
    setAdding(false);
  };

  const handleAdd = async () => {
    const number = draft.project_number.trim();
    const name = draft.name.trim();
    if (!number || !name) {
      toast({ title: 'Project number and name are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { data: proj } = await axios.post(
        `${API}/projects`,
        { project_number: number, name, customer_id: customerId, status: 'active' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      let qbToastNote = '';
      if (draft.also_push && qbConnected) {
        try {
          const { data: pushed } = await axios.post(
            `${API}/projects/${proj.id}/push-to-quickbooks`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (pushed?.qb_project_id) {
            qbToastNote = ` · pushed to QBO`;
          }
        } catch (qe) {
          // Local save succeeded — QBO push failure shouldn't block the UX
          qbToastNote = ` · QBO push failed: ${qe.response?.data?.detail || qe.message}`;
        }
      }
      toast({ title: `Project "${number}" added${qbToastNote}` });
      resetDraft();
      fetchProjects();
    } catch (e) {
      toast({ title: 'Could not add project', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (proj) => {
    if (!window.confirm(`Archive project "${proj.project_number} — ${proj.name}"? It will be hidden from new jobs but kept on historical ones.`)) return;
    try {
      await axios.delete(`${API}/projects/${proj.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Project archived' });
      fetchProjects();
    } catch (e) {
      toast({ title: 'Could not archive', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handlePushExisting = async (proj) => {
    try {
      const { data } = await axios.post(
        `${API}/projects/${proj.id}/push-to-quickbooks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: `Pushed to QBO`, description: `QBO project #${data.qb_project_id}` });
      fetchProjects();
    } catch (e) {
      toast({ title: 'Push to QBO failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  if (!customerId) {
    return (
      <div className="text-xs text-slate-500 italic py-2" data-testid="customer-projects-pending">
        Save the customer first to start adding projects (Plumbing, HVAC, etc.) — they&apos;ll mirror to QBO as sub-customers.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="customer-projects-inline">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-emerald-400" />
          <Label className="text-sm">Projects ({projects.length})</Label>
        </div>
        {!adding && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            className="h-7 text-xs"
            data-testid="customer-project-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add project
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 flex items-center gap-1.5 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading projects…
        </div>
      ) : projects.length === 0 && !adding ? (
        <p className="text-[11px] text-slate-500 italic py-1">
          No projects yet. Add ones like &quot;Plumbing&quot;, &quot;HVAC Q3&quot;, &quot;Janitorial&quot; to track them separately in QBO invoices and reports.
        </p>
      ) : (
        <ul className="space-y-1 max-h-44 overflow-y-auto pr-1" data-testid="customer-projects-list">
          {projects.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-[#0F1923] border border-[#2D3748]"
              data-testid={`customer-project-${p.id}`}
            >
              <span className="text-xs font-mono text-emerald-400 flex-shrink-0">{p.project_number}</span>
              <span className="text-sm text-white flex-1 min-w-0 truncate">{p.name}</span>
              {p.qb_project_id ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 flex items-center gap-1 flex-shrink-0" title={`QBO sub-customer #${p.qb_project_id}`}>
                  <CheckCircle2 className="w-2.5 h-2.5" /> QBO
                </span>
              ) : (
                qbConnected && (
                  <button
                    type="button"
                    onClick={() => handlePushExisting(p)}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 hover:bg-blue-500/30 flex items-center gap-1 flex-shrink-0"
                    title="Push to QBO as sub-customer"
                    data-testid={`customer-project-push-${p.id}`}
                  >
                    <RefreshCw className="w-2.5 h-2.5" /> Push
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => handleArchive(p)}
                className="text-slate-400 hover:text-amber-300 flex-shrink-0"
                title="Archive project"
                data-testid={`customer-project-archive-${p.id}`}
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="space-y-2 p-3 rounded bg-[#0F1923] border border-emerald-500/30" data-testid="customer-project-add-form">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <Label className="text-[11px]">Project # *</Label>
              <Input
                value={draft.project_number}
                onChange={(e) => setDraft({ ...draft, project_number: e.target.value })}
                placeholder="2025060"
                className="h-9 text-sm"
                data-testid="customer-project-number-input"
                disabled={saving}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px]">Name *</Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Plumbing, HVAC Q3, Janitorial…"
                className="h-9 text-sm"
                data-testid="customer-project-name-input"
                disabled={saving}
              />
            </div>
          </div>
          <label className={`flex items-center gap-2 text-xs ${qbConnected ? 'text-slate-300' : 'text-slate-500'}`}>
            <input
              type="checkbox"
              checked={draft.also_push}
              onChange={(e) => setDraft({ ...draft, also_push: e.target.checked })}
              disabled={!qbConnected || saving}
              className="accent-emerald-500"
              data-testid="customer-project-also-push"
            />
            Also create in QuickBooks
            {!qbConnected && <span className="text-[10px] text-slate-500 italic">(QBO not connected)</span>}
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={resetDraft} disabled={saving}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              disabled={saving || !draft.project_number.trim() || !draft.name.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="customer-project-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
